/**
 * "Data fall" background renderer.
 *
 * Sparse columns of engineering glyphs drifting downward, meant to sit behind
 * an otherwise quiet dark layout. Deliberately restrained: the head of each
 * column never goes above a low alpha, so it reads as instrument readout noise
 * rather than the high-contrast matrix rain.
 *
 * Free of React and of any imports so it can also be loaded by a static preview.
 */

const MAX_DPR = 2;

// Tuning knobs. Everything about how loud the background reads lives here.
const DEFAULTS = {
  glyphSize: 14,
  columnStep: 20,
  activeRatio: 0.72,
  minLength: 8,
  maxLength: 18,
  minSpeed: 0.28,
  maxSpeed: 0.78,
  minAlpha: 0.05,
  maxAlpha: 0.15,
  headAlpha: 0.3,
};

// Hex digits plus a few engineering marks, so the field reads as readouts
// rather than plain binary rain.
const GLYPHS = '0123456789ABCDEFµΔ∑≈√Ωπ◢◣';

const rand = (min, max) => min + Math.random() * (max - min);

const pickGlyph = (seed, row) => {
  const roll = Math.abs(Math.sin(seed + row * 0.83 + row * 1.7));
  return GLYPHS.charAt(Math.floor(roll * 97) % GLYPHS.length);
};

const createDataRain = (canvas, options = {}) => {
  const settings = { ...DEFAULTS, ...options };
  const ctx = canvas.getContext('2d');

  let width = 0;
  let height = 0;
  let columns = [];
  let rafId = null;
  let shouldRun = false;
  let lastFrame = 0;

  const makeColumn = (index, seeded) => {
    const active = Math.random() < settings.activeRatio;
    return {
      x: index * settings.columnStep + settings.columnStep * 0.5,
      y: seeded ? rand(-height, height) : rand(-height * 1.4, -40),
      speed: rand(settings.minSpeed, settings.maxSpeed),
      length: Math.round(rand(settings.minLength, settings.maxLength)),
      alpha: rand(settings.minAlpha, settings.maxAlpha),
      active,
      seed: rand(0, 100),
    };
  };

  const buildColumns = () => {
    const count = Math.max(8, Math.ceil(width / settings.columnStep));
    columns = Array.from({ length: count }, (_, i) => makeColumn(i, true));
  };

  const measure = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    width = canvas.clientWidth || window.innerWidth;
    height = canvas.clientHeight || window.innerHeight;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  const draw = () => {
    ctx.clearRect(0, 0, width, height);
    ctx.font = `${settings.glyphSize}px "SF Mono", "Fira Code", Consolas, monospace`;
    ctx.textAlign = 'center';

    for (let i = 0; i < columns.length; i += 1) {
      const column = columns[i];
      if (!column.active) continue;

      for (let row = 0; row < column.length; row += 1) {
        const y = column.y - row * settings.glyphSize;
        if (y < -settings.glyphSize || y > height + settings.glyphSize) continue;

        // Squared falloff keeps the tail from smearing into a solid block.
        const fade = 1 - row / column.length;
        const alpha = column.alpha * fade * fade;
        if (alpha <= 0.005) continue;

        ctx.fillStyle =
          row === 0
            ? `rgba(100, 255, 218, ${Math.min(settings.headAlpha, alpha * 2.6).toFixed(3)})`
            : `rgba(148, 163, 196, ${alpha.toFixed(3)})`;
        ctx.fillText(pickGlyph(column.seed, row + Math.floor(column.y / 48)), column.x, y);
      }
    }
  };

  const advance = dt => {
    for (let i = 0; i < columns.length; i += 1) {
      const column = columns[i];
      if (!column.active) continue;
      column.y += column.speed * dt * 0.55;

      if (column.y - column.length * settings.glyphSize > height) {
        column.y = rand(-420, -40);
        column.speed = rand(settings.minSpeed, settings.maxSpeed);
        column.length = Math.round(rand(settings.minLength, settings.maxLength));
        column.alpha = rand(settings.minAlpha, settings.maxAlpha);
        column.seed = rand(0, 100);
      }
    }
  };

  const tick = now => {
    // dt is expressed in 60fps frames so the drift holds up on any refresh rate.
    const dt = lastFrame ? Math.min(now - lastFrame, 32) / (1000 / 60) : 1;
    lastFrame = now;
    advance(dt);
    draw();
    rafId = window.requestAnimationFrame(tick);
  };

  const start = () => {
    shouldRun = true;
    if (rafId !== null) return;
    lastFrame = 0;
    rafId = window.requestAnimationFrame(tick);
  };

  const stop = () => {
    shouldRun = false;
    if (rafId === null) return;
    window.cancelAnimationFrame(rafId);
    rafId = null;
  };

  const renderOnce = () => {
    stop();
    draw();
  };

  const handleResize = () => {
    measure();
    buildColumns();
    if (rafId === null) draw();
  };

  const handleVisibility = () => {
    if (document.hidden) {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    } else if (shouldRun && rafId === null) {
      lastFrame = 0;
      rafId = window.requestAnimationFrame(tick);
    }
  };

  const destroy = () => {
    stop();
    window.removeEventListener('resize', handleResize);
    document.removeEventListener('visibilitychange', handleVisibility);
  };

  measure();
  buildColumns();
  window.addEventListener('resize', handleResize);
  document.addEventListener('visibilitychange', handleVisibility);

  return { start, stop, renderOnce, draw, resize: handleResize, destroy };
};

export default createDataRain;
